import React, { useState, useRef, useEffect } from 'react';
import { Button, Card } from 'antd';
import styles from './index.module.scss';
import { Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';


interface Language {
  code: string;
  name: string;
  flag: string;
  videoUrl: string;
}

const HumanShow: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { t } = useTranslation();

  const languages: Language[] = [
    {
      code: 'en',
      name: 'English',
      flag: '🇺🇸',
      videoUrl: 'https://liehuzuo-web.oss-cn-beijing.aliyuncs.com/9942a177-c1f7-442b-b231-f001e98c3a3b/humans/videos/sDbePiMjX6.mp4'
    },
    {
      code: 'ja',
      name: '日本語',
      flag: '🇯🇵',
      videoUrl: 'https://liehuzuo-web.oss-cn-beijing.aliyuncs.com/9942a177-c1f7-442b-b231-f001e98c3a3b/humans/videos/X3rjo4v6Wy.mp4'
    },
    {
      code: 'ko',
      name: '한국어',
      flag: '🇰🇷',
      videoUrl: 'https://liehuzuo-web.oss-cn-beijing.aliyuncs.com/9942a177-c1f7-442b-b231-f001e98c3a3b/humans/videos/BXjivGgz2U.mp4'
    },
    
    {
      code: 'yue',
      name: '粤语',
      flag: '🇭🇰',
      videoUrl: 'https://liehuzuo-web.oss-cn-beijing.aliyuncs.com/9942a177-c1f7-442b-b231-f001e98c3a3b/humans/videos/h7uxIQLrwZ.mp4'
    },
    {
      code: 'zh',
      name: '中文',
      flag: '🇨🇳',
      videoUrl: 'https://liehuzuo-web.oss-cn-beijing.aliyuncs.com/9942a177-c1f7-442b-b231-f001e98c3a3b/humans/videos/JPAjNMam98.mp4'
    }
  ];

  const currentLanguage = languages.find(lang => lang.code === selectedLanguage);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    if (videoRef.current) {
      setIsVideoReady(false);
      videoRef.current.load();
    }
  }, [selectedLanguage]);

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguage(languageCode);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVideoReady = () => {
    setIsVideoReady(true);
  };

  return (
    <section className={styles.digitalHuman}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('index.human_show.title')}</h2>
          <p className={styles.subtitle}>{t('index.human_show.sub_title')}</p>
        </div>

        <div className={styles.content}>
          {/* 语言选择器 */}
          <div className={styles.languageSelector}>
            <h3 className={styles.selectorTitle}>{t('index.human_show.choose_lang')}</h3>
            <div className={styles.languageGrid}>
              {languages.map((language) => (
                <Card
                  key={language.code}
                  className={`${styles.languageCard} ${
                    selectedLanguage === language.code ? styles.active : ''
                  }`}
                  onClick={() => handleLanguageSelect(language.code)}
                  hoverable
                >
                  <div className={styles.languageContent}>
                    <span className={styles.flag}>{language.flag}</span>
                    <span className={styles.languageName}>{t(`languages.${language.code}`)}</span>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* 视频播放器 */}
          <div className={styles.videoSection}>
            <div className={styles.videoContainer}>
              <div className={styles.videoWrapper}>
                <video
                  ref={videoRef}
                  className={styles.video}
                  autoPlay
                  loop
                  muted={isMuted}
                  playsInline
                  onCanPlay={handleVideoReady}
                  poster="/images/digital-human-poster.jpg"
                >
                  <source src={currentLanguage?.videoUrl} type="video/mp4" />
                  您的浏览器不支持视频播放。
                </video>
                
                {!isVideoReady && (
                  <div className={styles.videoLoading}>
                    <div className={styles.loadingSpinner}></div>
                    <p>加载中...</p>
                  </div>
                )}

                {/* 视频控制按钮 */}
                <div className={styles.videoControls}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />} 
                    onClick={toggleMute}
                    className={styles.muteButton}
                  />
                </div>
              </div>

              {/* 当前语言信息 */}
              <div className={styles.currentLanguageInfo}>
                <span className={styles.currentFlag}>{currentLanguage?.flag}</span>
                <span className={styles.currentName}>{t(`languages.${selectedLanguage}`)}</span>
                <span className={styles.statusText}>
                  {isVideoReady ? t('index.human_show.playing') : t('index.loading')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HumanShow;